/**
 * Navigation Configuration
 * 
 * Single source of truth for navigation items across all shells.
 * Prevents drift between BottomNav, TopBar, PublicHeader, and future components.
 * 
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

import { Home, Users, Calendar, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ===============================
// TYPE DEFINITIONS
// ===============================

export interface MainNavItem {
  id: string;
  path: string;
  label: string;
  icon: LucideIcon;
  show?: boolean; // Optional conditional visibility
}

export interface PublicNavItem {
  path: string;
  label: string;
  show?: boolean; // Optional conditional visibility
}

// ===============================
// AUTHENTICATED APP NAVIGATION
// ===============================

/**
 * Main navigation items for authenticated users
 * Used by: BottomNav (mobile), TopBar (desktop)
 */
export const MAIN_NAV_ITEMS: MainNavItem[] = [
  {
    id: 'home',
    path: '/home',
    label: 'Home',
    icon: Home,
  },
  {
    id: 'clubs',
    path: '/clubs',
    label: 'Clubs',
    icon: Users,
  },
  {
    id: 'events',
    path: '/events',
    label: 'Events',
    icon: Calendar,
  },
  {
    id: 'you',
    path: '/me',
    label: 'You',
    icon: User,
  },
];

// ===============================
// PUBLIC/MARKETING NAVIGATION
// ===============================

/**
 * Public page navigation items
 * Used by: PublicHeader (desktop nav + mobile hamburger)
 */
export const PUBLIC_NAV_ITEMS: PublicNavItem[] = [
  {
    path: '/',
    label: 'Home',
  },
  {
    path: '/faq',
    label: 'FAQ',
  },
  {
    path: '/about',
    label: 'About',
  },
  {
    path: '/privacy',
    label: 'Privacy',
  },
  {
    path: '/terms',
    label: 'Terms',
  },
  {
    path: '/contact',
    label: 'Contact',
  },
];

// ===============================
// UTILITY FUNCTIONS
// ===============================

/**
 * Filter navigation items based on show property
 * Useful for feature flags or role-based visibility
 */
export function filterVisibleItems<T extends { show?: boolean }>(items: T[]): T[] {
  return items.filter(item => item.show !== false);
}

/**
 * Find navigation item by path
 */
export function findNavItemByPath(path: string): MainNavItem | PublicNavItem | undefined {
  return [...MAIN_NAV_ITEMS, ...PUBLIC_NAV_ITEMS].find(item => item.path === path);
}

/**
 * Get main nav item by ID
 */
export function getMainNavItemById(id: string): MainNavItem | undefined {
  return MAIN_NAV_ITEMS.find(item => item.id === id);
}